import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'shared/components/UC/Button';
import { EvalByEvaluatorAssignment } from '../../types/admin';
import styles from './AssignmentOverflowMenu.module.css';

interface AssignmentOverflowMenuProps {
  assignmentId: string;
  applicationId: string;
  status: EvalByEvaluatorAssignment['status'];
  onViewApplication: (applicationId: string) => void;
  onRemind: () => void;
  onReassign: () => void;
}

interface MenuItem {
  label: string;
  isDanger?: boolean;
  action: () => void;
}

const AssignmentOverflowMenu: React.FC<AssignmentOverflowMenuProps> = ({
  assignmentId: _assignmentId,
  applicationId,
  status,
  onViewApplication,
  onRemind,
  onReassign,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const items: MenuItem[] = React.useMemo(() => {
    const viewApplication: MenuItem = {
      label: 'Ver evaluaciones',
      action: () => {
        setIsOpen(false);
        onViewApplication(applicationId);
      },
    };
    const remindInvitation: MenuItem = {
      label: 'Recordar invitación',
      action: () => { setIsOpen(false); onRemind(); },
    };
    const remindEvaluation: MenuItem = {
      label: 'Recordar evaluación',
      action: () => { setIsOpen(false); onRemind(); },
    };
    const reassign: MenuItem = {
      label: 'Reasignar',
      isDanger: true,
      action: () => { setIsOpen(false); onReassign(); },
    };

    switch (status) {
      case 'pending':
        return [remindInvitation, viewApplication];
      case 'accepted_not_started':
      case 'in_progress':
        return [remindEvaluation, viewApplication, reassign];
      case 'completed':
        return [viewApplication];
      default:
        return [viewApplication];
    }
  }, [status, applicationId, onViewApplication, onRemind, onReassign]);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right });
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setIsOpen(false); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <div ref={buttonRef} className={styles.wrapper}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        Acciones
        <i className="uc-icon icon-size--sm" style={{ marginLeft: '0.25rem' }}>expand_more</i>
      </Button>

      {isOpen && menuPos && (
        <ul
          role="menu"
          className={styles.menu}
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            transform: 'translateX(-100%)',
          }}
        >
          {items.map((item) => (
            <li key={item.label} role="none">
              <button
                type="button"
                role="menuitem"
                className={`${styles.menuItem} ${item.isDanger ? styles.menuItemDanger : ''}`}
                onClick={item.action}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AssignmentOverflowMenu;
